import { create } from "zustand";

/** Unsubmitted a2ui form input, keyed by `UiRenderer`'s principal-scoped render
    key. Holds typed clinical text, so entries must not outlive their author. */
interface UiModelState {
  models: Record<string, Record<string, unknown>>;
  submitting: Record<string, boolean>;
  init: (renderId: string, initial?: Record<string, unknown>) => void;
  set: (renderId: string, bind: string, value: unknown) => void;
  reset: (renderId: string) => void;
  setSubmitting: (renderId: string, value: boolean) => void;
  clearAll: () => void;
}

export const useUiModelStore = create<UiModelState>((set) => ({
  models: {},
  submitting: {},
  init: (renderId, initial = {}) =>
    set((s) =>
      s.models[renderId] ? s : { models: { ...s.models, [renderId]: initial } },
    ),
  set: (renderId, bind, value) =>
    set((s) => ({
      models: {
        ...s.models,
        [renderId]: { ...s.models[renderId], [bind]: value },
      },
    })),
  // Drop the entry so the next `init` re-seeds from the fresh payload —
  // otherwise a same-card refresh (ask_changes) would carry the submitted field
  // text onto the returned pending card.
  reset: (renderId) =>
    set((s) => {
      const { [renderId]: _drop, ...rest } = s.models;
      return { models: rest };
    }),
  setSubmitting: (renderId, value) =>
    set((s) => ({
      submitting: { ...s.submitting, [renderId]: value },
    })),
  // `submitting` too — a card left mid-flight would otherwise render the next
  // principal's form disabled with no way back.
  clearAll: () => set({ models: {}, submitting: {} }),
}));
